import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Star, ChevronLeft, ChevronRight, Quote, Loader2 } from 'lucide-react';

const TestimonialsSection = () => {
  const { t, isRTL } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/reviews`);
        if (response.ok) {
          const data = await response.json();
          const apiReviews = data.map(review => ({
            name: review.name,
            quote: review.content || review.title,
            rating: review.rating || 5,
            program: review.program_name || 'Healing Journey',
            location: review.city || 'Egypt',
            isFromAPI: true
          }));
          if (apiReviews.length > 0) {
            setReviews(apiReviews);
          } else {
            setReviews(t.testimonials.items);
          }
        } else {
          setReviews(t.testimonials.items);
        }
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setReviews(t.testimonials.items);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, [backendUrl, t.testimonials.items]);

  const nextTestimonial = () => {
    if (reviews.length > 0) setActiveIndex((prev) => (prev + 1) % reviews.length);
  };
  const prevTestimonial = () => {
    if (reviews.length > 0) setActiveIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchMove  = (e) => { touchEndX.current  = e.touches[0].clientX; };
  const handleTouchEnd   = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) { isRTL ? prevTestimonial() : nextTestimonial(); }
      else          { isRTL ? nextTestimonial() : prevTestimonial(); }
    }
  };

  if (loading) {
    return (
      <section id="testimonials" className="section-padding bg-charcoal-100">
        <div className="container mx-auto px-4 flex justify-center items-center py-20">
          <Loader2 className="animate-spin text-gold" size={32} />
        </div>
      </section>
    );
  }
  if (reviews.length === 0) return null;

  const currentTestimonial = reviews[activeIndex];
  const initial = (name) => name?.charAt(0)?.toUpperCase() || 'A';

  return (
    <section id="testimonials" className="section-padding bg-charcoal-100">
      <div className="container mx-auto px-4 sm:px-6 lg:px-10">

        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-16">
          <p className="text-gold text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-3 sm:mb-4">
            {t.testimonials.tagline}
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-cream leading-tight mb-4 sm:mb-6 px-2">
            {t.testimonials.headline}
          </h2>
          <div className="gold-divider" />
          <p className="text-cream-300 mt-6 sm:mt-8 text-sm sm:text-base px-4">
            {t.testimonials.description}
          </p>
        </div>

        {/* ── Large featured / active testimonial card ── */}
        <div className="max-w-4xl mx-auto mb-8 sm:mb-12">
          <div
            className="relative p-6 sm:p-10 lg:p-14 border border-gold/25 bg-charcoal rounded-2xl overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Decorative top gold line */}
            <div className="absolute top-0 left-10 right-10 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />

            {/* Large quote icon */}
            <div className={`absolute top-5 sm:top-7 ${isRTL ? 'right-5 sm:right-7' : 'left-5 sm:left-7'}`}>
              <Quote size={44} className="text-gold/18 sm:w-16 sm:h-16" />
            </div>

            <div className="relative z-10">
              {/* Stars */}
              <div className="flex gap-1.5 justify-center mb-6 sm:mb-8">
                {[...Array(currentTestimonial.rating || 5)].map((_, i) => (
                  <Star key={i} size={15} className="text-gold fill-gold sm:w-5 sm:h-5" />
                ))}
              </div>

              {/* Quote — larger, more generous line-height */}
              <blockquote className="text-base sm:text-xl lg:text-2xl text-cream text-center leading-[1.75] sm:leading-[1.8] mb-8 sm:mb-10 font-light italic px-2 sm:px-8">
                "{currentTestimonial.quote}"
              </blockquote>

              {/* Author */}
              <div className="text-center">
                {/* Avatar */}
                <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-gold/15 rounded-full flex items-center justify-center ring-1 ring-gold/40 ring-offset-2 ring-offset-charcoal">
                  <span className="text-gold font-serif text-2xl sm:text-3xl">
                    {initial(currentTestimonial.name)}
                  </span>
                </div>

                {/* Name */}
                <p className="text-cream font-semibold text-sm sm:text-base tracking-wide">
                  {currentTestimonial.name}
                </p>

                {/* Program pill */}
                {currentTestimonial.program && (
                  <span className="inline-block mt-1.5 px-3 py-0.5 text-xs text-gold/90 border border-gold/30 rounded-full">
                    {currentTestimonial.program}
                  </span>
                )}

                {/* Location */}
                {currentTestimonial.location && (
                  <p className="text-cream-400 text-xs mt-1.5">
                    {currentTestimonial.location}
                  </p>
                )}
              </div>
            </div>

            {/* Swipe hint — mobile only */}
            <div className="sm:hidden text-center mt-5 text-cream-500 text-xs">
              {isRTL ? 'اسحب للتنقل' : 'Swipe to navigate'}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 sm:gap-6 mt-6 sm:mt-8">
            <button
              onClick={prevTestimonial}
              className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center border border-white/20 text-cream-300 hover:border-gold hover:text-gold transition-all active:scale-95 rounded-lg"
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={20} className={isRTL ? 'rotate-180' : ''} />
            </button>

            <div className="flex gap-2 items-center">
              {reviews.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`h-2 transition-all rounded-full ${
                    index === activeIndex
                      ? 'bg-gold w-6 sm:w-8'
                      : 'bg-white/30 hover:bg-white/50 w-2'
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center border border-white/20 text-cream-300 hover:border-gold hover:text-gold transition-all active:scale-95 rounded-lg"
              aria-label="Next testimonial"
            >
              <ChevronRight size={20} className={isRTL ? 'rotate-180' : ''} />
            </button>
          </div>
        </div>

        {/* ── Grid cards — desktop/tablet only ── */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {reviews.slice(0, 6).map((testimonial, index) => {
            const isActive    = index === activeIndex;
            const isFeatured  = index === 0;

            return (
              <div
                key={index}
                className={`relative p-5 sm:p-6 border cursor-pointer transition-all rounded-xl ${
                  isActive
                    ? 'border-gold/60 bg-gold/5'
                    : isFeatured
                    ? 'border-gold/30 bg-charcoal hover:border-gold/50'
                    : 'border-white/10 bg-charcoal hover:border-gold/25'
                }`}
                onClick={() => setActiveIndex(index)}
              >
                {/* Featured badge — only on index 0 when not active */}
                {isFeatured && !isActive && (
                  <span className="absolute top-4 right-4 text-gold/70 text-xs tracking-wider">✦</span>
                )}

                {/* Stars */}
                <div className="flex gap-1 mb-3 sm:mb-4">
                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <Star key={i} size={12} className="text-gold fill-gold" />
                  ))}
                </div>

                {/* Quote */}
                <p className="text-cream-300 text-sm leading-relaxed line-clamp-3 mb-4 italic">
                  "{testimonial.quote}"
                </p>

                {/* Author row */}
                <div className="flex items-center gap-3 pt-2 border-t border-white/10">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isFeatured ? 'bg-gold/20 ring-1 ring-gold/40' : 'bg-gold/15'
                  }`}>
                    <span className="text-gold text-sm font-medium">{initial(testimonial.name)}</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-cream text-sm font-medium truncate">{testimonial.name}</p>
                    <p className="text-cream-400 text-xs truncate">{testimonial.program}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile page indicator */}
        <div className="md:hidden text-center mt-4">
          <p className="text-cream-400 text-xs">
            {activeIndex + 1} / {reviews.length}
          </p>
        </div>

      </div>
    </section>
  );
};

export default TestimonialsSection;
